import {
  CashFlowAnalysisSchema,
  CreditApplicationSchema,
  DecisionRecommendationSchema,
  PhaseChangeSchema,
  type CreditApplication,
} from '@paguz/contracts';
import { z } from 'zod';
import {
  AdminApplicationDetailSchema,
  AdminApplicationPageSchema,
  DecisionResultSchema,
  type AdminApplicationDetail,
  type AdminApplicationSummary,
} from './origination-schemas.ts';

/**
 * Alineación con la API real (`services/api` módulo `origination`, `/v1/admin/applications*`): la
 * cola llega como `{ items }` con el expediente completo y el detalle trae `risk` resumido. Aquí se
 * normalizan a las vistas de la consola; lo que la API aún no entrega (memo del copiloto, ruta de
 * política, conversaciones, scorecard) queda vacío. Cada esquema acepta también la vista de MSW.
 */

const ApplicantSchema = z.object({
  customerId: z.string(),
  fullName: z.string(),
  maskedDocument: z.string(),
  maskedPhone: z.string(),
});

const text = (value: unknown) => (typeof value === 'string' && value !== '' ? value : null);

function summaryOf(
  app: CreditApplication,
  applicant: z.infer<typeof ApplicantSchema> | undefined,
): AdminApplicationSummary {
  const profile = app.businessProfile as Record<string, unknown>;
  return {
    id: app.id,
    applicant: {
      customerId: app.customerId,
      fullName: applicant?.fullName ?? 'Cliente',
      maskedDocument: applicant?.maskedDocument ?? '—',
    },
    businessName: text(profile.businessName),
    businessType: text(profile.businessType),
    city: text(profile.municipalityName) ?? text(profile.municipality),
    phase: app.phase,
    status: app.status,
    requestedAmount: app.requestedAmount,
    suggestedLimit: null,
    recommendation: null,
    policyRoute: null,
    alerts: 0,
    phaseEnteredAt: app.lastActivityAt,
    assignedTo: null,
  };
}

export const ApplicationListResponseSchema = z.union([
  AdminApplicationPageSchema,
  z
    .object({
      items: z.array(
        CreditApplicationSchema.and(z.object({ applicant: ApplicantSchema.optional() })),
      ),
      nextCursor: z.string().nullable(),
    })
    .transform((page) => ({
      data: page.items.map((item) => summaryOf(item, item.applicant)),
      nextCursor: page.nextCursor,
    })),
]);

const RealDetailSchema = z.object({
  application: CreditApplicationSchema,
  applicant: ApplicantSchema.optional(),
  risk: z
    .looseObject({
      bureauScore: z.number().nullable().optional(),
      thinFile: z.boolean().optional(),
      currentDelinquencyDays: z.number().optional(),
      sanctions: z.string().optional(),
      identity: z.string().optional(),
    })
    .nullable()
    .optional(),
  // La creación del expediente llega con `from: null` (la vista la muestra como entrada a la fase).
  phaseHistory: z.array(
    z
      .looseObject({ from: z.string().nullable(), to: z.string() })
      .transform((change) =>
        PhaseChangeSchema.parse({ ...change, from: change.from ?? change.to }),
      ),
  ),
  cashFlow: CashFlowAnalysisSchema.nullable(),
  recommendation: DecisionRecommendationSchema.nullable(),
});

function realDetailToView(real: z.infer<typeof RealDetailSchema>): AdminApplicationDetail {
  const { application, risk, cashFlow } = real;
  const enteredAt = real.phaseHistory.at(-1)?.at ?? application.lastActivityAt;
  const zero = { amount: '0.00', currency: 'COP' as const };
  return {
    application,
    applicant: {
      customerId: application.customerId,
      fullName: real.applicant?.fullName ?? 'Cliente',
      maskedDocument: real.applicant?.maskedDocument ?? '—',
      maskedPhone: real.applicant?.maskedPhone ?? '—',
      age: null,
      city: summaryOf(application, real.applicant).city,
    },
    consents: [],
    phaseEnteredAt: enteredAt,
    phaseHistory: real.phaseHistory,
    assignedTo: null,
    cashFlow,
    risk: {
      bureau:
        risk?.bureauScore !== undefined
          ? {
              provider: 'Central de riesgo',
              consultedAt: enteredAt,
              score: risk.bureauScore === null ? null : Math.round(risk.bureauScore),
              rating: null,
              thinFile: risk.thinFile ?? false,
              currentDelinquencyDays: Math.round(risk.currentDelinquencyDays ?? 0),
              inquiriesLast6Months: 0,
              totalDebt: zero,
              monthlyPayments: cashFlow?.existingDebtPayments ?? zero,
              obligations: [],
            }
          : null,
      kyc:
        risk?.identity !== undefined
          ? {
              provider: 'KYC',
              checkedAt: enteredAt,
              status:
                risk.identity === 'VERIFIED'
                  ? 'VERIFIED'
                  : risk.identity === 'FAILED'
                    ? 'FAILED'
                    : 'PENDING',
              documentMatch: risk.identity === 'VERIFIED',
              livenessScore: null,
              sanctions:
                risk.sanctions === 'MATCH'
                  ? 'MATCH'
                  : risk.sanctions === 'REVIEW'
                    ? 'REVIEW'
                    : 'CLEAR',
              listsChecked: [],
            }
          : null,
      scorecard: [],
    },
    recommendation: real.recommendation,
    policyRoute: null,
    copilotMemo: null,
    conversations: [],
    history: [],
  };
}

export const ApplicationDetailResponseSchema = z.union([
  AdminApplicationDetailSchema,
  RealDetailSchema.transform(realDetailToView),
]);

/** La API real responde la decisión con el expediente del cliente: la consola recarga el detalle. */
export const DecisionResponseSchema = z.union([
  DecisionResultSchema,
  CreditApplicationSchema.transform((app) => ({
    detail: null,
    message:
      app.status === 'APPROVED'
        ? 'Decisión registrada: aprobado. El Asesor IA se lo comunica al cliente.'
        : 'Decisión registrada.',
  })),
]);
